import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  CircularProgress,
  InputAdornment,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Send as SendIcon,
  Add as AddIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  ChatBubbleOutline as ChatIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  setLoading,
  setLoadingMessages,
  setLoadingOlderMessages,
  setSendingMessage,
  setError,
  setChats,
  appendChats,
  setSelectedChatId,
  setMessages,
  prependMessages,
  addMessage,
  setSearch,
  setChatPage,
  addChatToTop,
  deleteChat as deleteChatAction,
  clearMessages,
  resetChatbot,
} from '../../store/slices/chatbotSlice';
import { GET_CHATBOT_CHATS, GET_CHATBOT_MESSAGES } from '../../graphql/queries/chatbot.queries';
import { CHAT_WITH_RAG, DELETE_CHAT } from '../../graphql/mutations/chatbot.mutations';
import {
  FindChatListInput,
  FindMessageListInput,
  ChatsListResponse,
  MessagesListResponse,
  ChatWithRAGInput,
  ChatWithRAGResponse,
  DeleteChatInput,
  DeleteChatResponse,
  Chat,
} from '../../types/chatbot.types';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import MainLayout from '../../components/Layout/MainLayout';
import './Chatbot.css';

const Chatbot: React.FC = () => {
  const dispatch = useAppDispatch();
  const {
    chats,
    selectedChatId,
    messages,
    loading,
    loadingMessages,
    loadingOlderMessages,
    sendingMessage,
    error,
    chatsPagination,
    messagesPagination,
    search,
  } = useAppSelector((state) => state.chatbot);

  const [searchInput, setSearchInput] = useState('');
  const [messageInput, setMessageInput] = useState('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Delete state
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [chatToDelete, setChatToDelete] = useState<Chat | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Context menu state
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuChatId, setMenuChatId] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesAreaRef = useRef<HTMLDivElement>(null);
  const chatListRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Queries & Mutations
  const [fetchChats] = useLazyQuery<
    { chatbotChats: ChatsListResponse },
    { params: FindChatListInput }
  >(GET_CHATBOT_CHATS, { fetchPolicy: 'network-only' });

  const [fetchMessages] = useLazyQuery<
    { chatbotMessages: MessagesListResponse },
    { params: FindMessageListInput }
  >(GET_CHATBOT_MESSAGES, { fetchPolicy: 'network-only' });

  const [chatWithRAGMutation] = useMutation<
    { chatWithRAG: ChatWithRAGResponse },
    { params: ChatWithRAGInput }
  >(CHAT_WITH_RAG);

  const [deleteChatMutation] = useMutation<
    { deleteChatbotChat: DeleteChatResponse },
    { params: DeleteChatInput }
  >(DELETE_CHAT);

  // Load chats on mount and search change
  useEffect(() => {
    loadChats(1, false);
    return () => {
      dispatch(resetChatbot());
    };
  }, []);

  useEffect(() => {
    loadChats(1, false);
  }, [search]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadChats = async (page: number, append: boolean) => {
    try {
      dispatch(setLoading(true));
      const { data } = await fetchChats({
        variables: {
          params: {
            search: search || null,
            page,
            pageSize: chatsPagination.pageSize,
          },
        },
      });
      if (data?.chatbotChats?.success && data.chatbotChats.data) {
        if (append) {
          dispatch(appendChats(data.chatbotChats.data));
        } else {
          dispatch(setChats(data.chatbotChats.data));
        }
        dispatch(setChatPage(page));
      } else {
        dispatch(setError(data?.chatbotChats?.message || 'Error al cargar chats'));
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
      dispatch(setError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const loadMessages = async (chatId: string) => {
    try {
      dispatch(setLoadingMessages(true));
      const { data } = await fetchMessages({
        variables: {
          params: {
            chatId,
            search: null,
            page: 1,
            pageSize: 50,
          },
        },
      });
      if (data?.chatbotMessages?.success && data.chatbotMessages.data) {
        dispatch(setMessages(data.chatbotMessages.data));
      } else {
        dispatch(setError(data?.chatbotMessages?.message || 'Error al cargar mensajes'));
        dispatch(setLoadingMessages(false));
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
      dispatch(setError(err.message || 'Error de conexión'));
      dispatch(setLoadingMessages(false));
    }
  };

  const loadOlderMessages = async () => {
    if (!selectedChatId || loadingOlderMessages || !messagesPagination.hasMorePages) return;
    const nextPage = messagesPagination.page + 1;
    try {
      dispatch(setLoadingOlderMessages(true));
      const el = messagesAreaRef.current;
      const prevScrollHeight = el?.scrollHeight || 0;

      const { data } = await fetchMessages({
        variables: {
          params: {
            chatId: selectedChatId,
            search: null,
            page: nextPage,
            pageSize: 50,
          },
        },
      });
      if (data?.chatbotMessages?.success && data.chatbotMessages.data) {
        dispatch(prependMessages(data.chatbotMessages.data));
        // Preserve scroll position after prepending
        requestAnimationFrame(() => {
          if (el) {
            el.scrollTop = el.scrollHeight - prevScrollHeight;
          }
        });
      } else {
        dispatch(setError(data?.chatbotMessages?.message || 'Error al cargar mensajes anteriores'));
        dispatch(setLoadingOlderMessages(false));
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
      dispatch(setError(err.message || 'Error de conexión'));
      dispatch(setLoadingOlderMessages(false));
    }
  };

  const handleChatSelect = (chatId: string) => {
    if (chatId === selectedChatId) return;
    dispatch(setSelectedChatId(chatId));
    loadMessages(chatId);
  };

  const handleNewConversation = () => {
    dispatch(clearMessages());
    setMessageInput('');
  };

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }
    searchTimerRef.current = setTimeout(() => {
      dispatch(setSearch(value));
    }, 400);
  };

  // Infinite scroll up for messages
  const handleMessagesScroll = useCallback(() => {
    const el = messagesAreaRef.current;
    if (!el) return;
    if (el.scrollTop < 50 && messagesPagination.hasMorePages && !loadingOlderMessages) {
      loadOlderMessages();
    }
  }, [messagesPagination.hasMorePages, loadingOlderMessages, selectedChatId, messagesPagination.page]);

  // Infinite scroll for chat list
  const handleChatListScroll = useCallback(() => {
    const el = chatListRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    if (scrollHeight - scrollTop - clientHeight < 50 && chatsPagination.hasMorePages && !loading) {
      loadChats(chatsPagination.page + 1, true);
    }
  }, [chatsPagination.hasMorePages, chatsPagination.page, loading]);

  // Send message
  const handleSendMessage = async () => {
    const trimmed = messageInput.trim();
    if (!trimmed || sendingMessage) return;

    const userMessage = {
      id: `temp-${Date.now()}`,
      chatId: selectedChatId || '',
      tenantId: '',
      userId: '',
      role: 'human' as const,
      content: trimmed,
      feedback: null,
      createdAt: new Date().toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
      }),
      createdBy: null,
    };

    dispatch(addMessage(userMessage));
    setMessageInput('');
    dispatch(setSendingMessage(true));

    try {
      const { data: responseData } = await chatWithRAGMutation({
        variables: {
          params: {
            chatId: selectedChatId || null,
            query: trimmed,
          },
        },
      });

      if (responseData?.chatWithRAG?.success && responseData.chatWithRAG.data) {
        const aiMessage = responseData.chatWithRAG.data;
        dispatch(addMessage(aiMessage));

        // If this was a new conversation, set the selected chat and add to sidebar
        if (!selectedChatId && aiMessage.chatId) {
          dispatch(setSelectedChatId(aiMessage.chatId));
          const newChat: Chat = {
            id: aiMessage.chatId,
            tenantId: aiMessage.tenantId,
            userId: aiMessage.userId,
            title: trimmed.length > 60 ? trimmed.substring(0, 60) + '...' : trimmed,
            lastMessageAt: aiMessage.createdAt,
            createdAt: aiMessage.createdAt,
            createdBy: aiMessage.createdBy,
          };
          dispatch(addChatToTop(newChat));
        } else if (selectedChatId) {
          // Update the chat's lastMessageAt in the sidebar
          const existingChat = chats.find((c) => c.id === selectedChatId);
          if (existingChat) {
            dispatch(addChatToTop({ ...existingChat, lastMessageAt: aiMessage.createdAt }));
          }
        }
      } else {
        const msg = responseData?.chatWithRAG?.message || 'Error al obtener respuesta del chatbot';
        dispatch(setError(msg));
      }
    } catch (err: any) {
      dispatch(setError(err.message || 'Error de conexión con el servidor'));
    } finally {
      dispatch(setSendingMessage(false));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Context menu handlers
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, chatId: string) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setMenuChatId(chatId);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuChatId(null);
  };

  const handleDeleteClick = () => {
    const chat = chats.find((c) => c.id === menuChatId);
    if (chat) {
      setChatToDelete(chat);
      setConfirmDeleteOpen(true);
      setDeleteError(null);
    }
    handleMenuClose();
  };

  const handleCancelDelete = () => {
    setConfirmDeleteOpen(false);
    setChatToDelete(null);
    setDeleteError(null);
  };

  const handleConfirmDelete = async () => {
    if (!chatToDelete) return;
    try {
      setDeleting(true);
      setDeleteError(null);
      const { data: responseData } = await deleteChatMutation({
        variables: { params: { chatId: chatToDelete.id } },
      });
      if (responseData?.deleteChatbotChat?.success) {
        dispatch(deleteChatAction(chatToDelete.id));
        setConfirmDeleteOpen(false);
        setChatToDelete(null);
        setSuccessMessage('Chat eliminado exitosamente');
      } else {
        const msg = responseData?.deleteChatbotChat?.message || 'Error al eliminar chat';
        const errs = responseData?.deleteChatbotChat?.errors || [];
        setDeleteError(errs.length > 0 ? `${msg}: ${errs.join(', ')}` : msg);
      }
    } catch (err: any) {
      setDeleteError(err.message || 'Error de conexión con el servidor');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <MainLayout>
      <div className="chatbot-container">
        {/* ===== LEFT SIDEBAR ===== */}
        <div className="chat-sidebar">
          <div className="chat-sidebar-header">
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              fullWidth
              className="new-chat-button"
              onClick={handleNewConversation}
            >
              Nueva Conversación
            </Button>
            <TextField
              size="small"
              placeholder="Buscar chat..."
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: '#999' }} />
                  </InputAdornment>
                ),
              }}
            />
          </div>

          <div
            className="chat-list-wrapper"
            ref={chatListRef}
            onScroll={handleChatListScroll}
          >
            {chats.map((chat) => (
              <div
                key={chat.id}
                className={`chat-list-item ${selectedChatId === chat.id ? 'active' : ''}`}
                onClick={() => handleChatSelect(chat.id)}
              >
                <div className="chat-list-item-content">
                  <span className="chat-list-item-title">{chat.title}</span>
                  <span className="chat-list-item-date">{chat.lastMessageAt}</span>
                </div>
                <IconButton
                  size="small"
                  onClick={(e) => handleMenuOpen(e, chat.id)}
                  sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </div>
            ))}

            {loading && (
              <div className="chat-list-loading">
                <CircularProgress size={24} />
              </div>
            )}

            {!loading && chats.length === 0 && (
              <div className="chat-list-empty">
                No hay conversaciones
              </div>
            )}
          </div>
        </div>

        {/* ===== RIGHT - CHAT WINDOW ===== */}
        <div className="chat-main">
          {/* Messages Area */}
          <div className="chat-messages-area" ref={messagesAreaRef} onScroll={handleMessagesScroll}>
            {!selectedChatId && messages.length === 0 && !loadingMessages ? (
              <div className="chat-empty-state">
                <ChatIcon />
                <Typography variant="h6" color="text.secondary">
                  Inicia una conversación
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Escribe un mensaje para comenzar a chatear con el asistente.
                </Typography>
              </div>
            ) : loadingMessages ? (
              <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {loadingOlderMessages && (
                  <Box display="flex" justifyContent="center" py={2}>
                    <CircularProgress size={24} />
                  </Box>
                )}

                {messages.filter((msg) => msg.role !== 'tool').map((msg) => (
                  <div key={msg.id} className={`chat-message ${msg.role}`}>
                    <div>
                      <div className="chat-message-bubble">
                        {msg.role === 'ai' ? (
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {msg.content}
                          </ReactMarkdown>
                        ) : (
                          msg.content
                        )}
                      </div>
                      <div className="chat-message-time">
                        {msg.createdAt}
                      </div>
                    </div>
                  </div>
                ))}

                {sendingMessage && (
                  <div className="typing-indicator">
                    <div className="dot" />
                    <div className="dot" />
                    <div className="dot" />
                  </div>
                )}

                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {/* Input Area */}
          <div className="chat-input-area">
            <TextField
              className="chat-input-field"
              placeholder="Escribe un mensaje..."
              value={messageInput}
              onChange={(e) => setMessageInput(e.target.value)}
              onKeyDown={handleKeyDown}
              multiline
              maxRows={6}
              inputRef={inputRef}
              disabled={sendingMessage}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '24px',
                  backgroundColor: '#f5f7fa',
                },
              }}
            />
            <IconButton
              className="send-button"
              onClick={handleSendMessage}
              disabled={!messageInput.trim() || sendingMessage}
            >
              <SendIcon />
            </IconButton>
          </div>
        </div>

        {/* Context Menu */}
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
            Eliminar
          </MenuItem>
        </Menu>

        {/* Delete Confirmation Dialog */}
        <Dialog open={confirmDeleteOpen} onClose={handleCancelDelete} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box display="flex" alignItems="center" gap={1}>
              <WarningIcon color="error" />
              Confirmar Eliminación
            </Box>
          </DialogTitle>
          <DialogContent>
            {deleteError && (
              <Alert severity="error" sx={{ mb: 2 }}>{deleteError}</Alert>
            )}
            <Typography>
              ¿Está seguro que desea eliminar esta conversación? Esta acción no se puede deshacer.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelDelete} variant="outlined" disabled={deleting}>
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmDelete}
              variant="contained"
              color="error"
              disabled={deleting}
              startIcon={deleting ? <CircularProgress size={20} /> : null}
            >
              {deleting ? 'Eliminando...' : 'Eliminar'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbars */}
        <Snackbar
          open={!!successMessage}
          autoHideDuration={6000}
          onClose={() => setSuccessMessage(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={() => setSuccessMessage(null)} severity="success" sx={{ width: '100%' }}>
            {successMessage}
          </Alert>
        </Snackbar>

        {error && (
          <Snackbar
            open={!!error}
            autoHideDuration={6000}
            onClose={() => dispatch(setError(null))}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Alert onClose={() => dispatch(setError(null))} severity="error" sx={{ width: '100%' }}>
              {error}
            </Alert>
          </Snackbar>
        )}
      </div>
    </MainLayout>
  );
};

export default Chatbot;
